import type { Editor, Range } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { ReactRenderer } from "@tiptap/react";
import type { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import type { ChipOption } from "../../types";
import { MAX_SUGGESTION_RESULTS } from "../../constants";
import { VariableChipList, type VariableChipListRef } from "./VariableChipList";

/**
 * Custom suggestion match finder for the `{{` trigger.
 *
 * Detects `{{` followed by optional word/dash characters and returns
 * the match range + query text. For example, typing `{{cust` will
 * return query = "cust".
 */
const findVariableChipMatch = (config: {
  char: string;
  $position: {
    pos: number;
    nodeBefore: { isText: boolean; text: string } | null;
  };
}): { range: { from: number; to: number }; query: string; text: string } | null => {
  const { char, $position } = config;
  const nodeBefore = $position.nodeBefore;

  if (!nodeBefore?.isText || !nodeBefore.text) return null;

  const text = nodeBefore.text;
  const textFrom = $position.pos - text.length;

  // Match {{ followed by word characters and dashes
  const regexp = new RegExp(`${escapeRegex(char)}([\\w-]*)$`);
  const match = text.match(regexp);

  if (!match || match.index === undefined) return null;

  const from = textFrom + match.index;
  const to = $position.pos;

  return {
    range: { from, to },
    query: match[1] || "",
    text: match[0],
  };
};

/** Escape special regex characters in a string */
const escapeRegex = (str: string): string =>
  str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Creates the suggestion configuration for the VariableChip extension.
 *
 * @param chipOptionsRef - Ref to the current chip options array.
 *   Using a ref allows the suggestion to always use the latest options
 *   without recreating the editor.
 */
export const createVariableChipSuggestion = (
  chipOptionsRef: React.RefObject<ChipOption[]>,
): Partial<SuggestionOptions<ChipOption>> => ({
  char: "{{",
  pluginKey: new PluginKey("variableChipSuggestion"),

  findSuggestionMatch: findVariableChipMatch as SuggestionOptions["findSuggestionMatch"],

  command: ({ editor, range, props }: { editor: Editor; range: Range; props: ChipOption }) => {
    editor
      .chain()
      .focus()
      .deleteRange(range)
      .insertVariableChip({
        id: props.id,
        label: props.label,
        value: props.value,
        bgColor: props.bgColor,
        fontColor: props.fontColor,
      })
      .run();
  },

  items: ({ query }: { query: string }): ChipOption[] => {
    const options = chipOptionsRef.current ?? [];
    const lowerQuery = query.toLowerCase();

    if (!lowerQuery) return options.slice(0, MAX_SUGGESTION_RESULTS);

    return options
      .filter((opt) => opt.label.toLowerCase().includes(lowerQuery))
      .slice(0, MAX_SUGGESTION_RESULTS);
  },

  render: () => {
    let component: ReactRenderer<VariableChipListRef>;
    let popup: TippyInstance[];

    return {
      onStart: (props: SuggestionProps<ChipOption>) => {
        component = new ReactRenderer(VariableChipList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) return;

        popup = tippy("body", {
          getReferenceClientRect: props.clientRect as () => DOMRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "bottom-start",
        });
      },

      onUpdate(props: SuggestionProps<ChipOption>) {
        component.updateProps(props);

        if (!props.clientRect) return;

        popup[0].setProps({
          getReferenceClientRect: props.clientRect as () => DOMRect,
        });
      },

      onKeyDown(props: { event: KeyboardEvent }): boolean {
        if (props.event.key === "Escape") {
          popup[0].hide();
          return true;
        }
        return component.ref?.onKeyDown(props.event) ?? false;
      },

      onExit() {
        popup[0]?.destroy();
        component?.destroy();
      },
    };
  },
});
